import { FrontendBIM360Issue } from './FrontendBIM360Issue';
import { BIM360IssueStatus, BIM360Issue } from '../BIM360Issue';
import * as _ from 'lodash';
import { classToClass } from 'class-transformer';
import { UIModel } from '@/UIModel';
import { FrontendForgeUser } from './FrontendForgeUser';
import { ForgeUser } from '../ForgeUser';
import { ProjectRole } from '../ProjectRole';
import { BIM360Company } from '../BIM360Company';

export type TokenFieldValue = { type: 'text' | 'token', contents: string }[];

export class BulkTemplateFieldWithTokens
{
    tokenFieldValue: TokenFieldValue;

    public constructor(initialValue: string)
    {
        this.tokenFieldValue = [{ type: 'text', contents: initialValue }];
    }

    public forIteration(index: number)
    {
        return this.tokenFieldValue
                   .map(t => (t.type == 'text') ? t.contents : index.toString())
                   .join('');
    }
}

interface HasToString
{
    toString(): string;
}

export class BulkTemplateField<TValue extends HasToString | null>
{
    public isChanged = false;

    public get value()
    {
        return this.getValue(this.issue);
    }

    public set value(newValue)
    {
        this.setValue(this.issue, newValue);
    }

    public valueForIssue(issue: BIM360Issue)
    {
        return this.getValue(issue);
    }

    public friendlyValueForIssue(issue: BIM360Issue)
    {
        let value = this.valueForIssue(issue);
        return this.getFriendlyValue(value, issue);
    }

    public updateIssue(issue: BIM360Issue)
    {
        this.setValue(issue, this.value);
    }

    public get isValidForBulkUpdate()
    {
        return !this.isChanged || this.value;
    }

    public constructor(public name: string, private issue: BIM360Issue, private getValue: (issue: BIM360Issue) => TValue, private setValue: (issue: BIM360Issue, value: TValue)=>void, private getFriendlyValue: (value: TValue, issue: BIM360Issue) => string = (value, issue) => value?.toString() || '')
    {
    }
}

export class BulkIssueTemplate
{
    title: BulkTemplateFieldWithTokens;
    description: BulkTemplateFieldWithTokens;
    locationDescription: BulkTemplateFieldWithTokens;
    model: UIModel;

    id = new BulkTemplateField('id', this.issue, (issue) => issue.id, (issue, value) => issue.id = value);
    forgeId = new BulkTemplateField('forgeId', this.issue, (issue) => issue.forgeId, (issue, value) => issue.forgeId = value);
    status = new BulkTemplateField('status', this.issue, (issue) => issue.status, (issue, value) => issue.status = value);
    permittedStatuses = new BulkTemplateField('permittedStatuses', this.issue, (issue) => issue.permittedStatuses, (issue, value) => issue.permittedStatuses = value);
    issueTypeId = new BulkTemplateField('issueTypeId', this.issue, (issue) => issue.issueTypeId, (issue, value) => issue.issueTypeId = value);
    issueSubtypeId = new BulkTemplateField('issueSubtypeId', this.issue, (issue) => issue.issueSubtypeId, (issue, value) => issue.issueSubtypeId = value, (issueSubtypeId) => this.getIssueTypeName(issueSubtypeId));
    assignedTo = new BulkTemplateField('assignedTo', this.issue, (issue) => issue.assignedTo, (issue, value) => issue.assignedTo = value, (assigneeId) => this.getAssigneeName(assigneeId));
    location = new BulkTemplateField('location', this.issue, (issue) => issue.location, (issue, value) => issue.location = value);
    owner = new BulkTemplateField('owner', this.issue, (issue) => issue.owner, (issue, value) => issue.owner = value, (ownerId) => this.getAssigneeName(ownerId));
    dueDate = new BulkTemplateField('dueDate', this.issue, (issue) => issue.dueDate, (issue, value) => issue.dueDate = value);
    rootCauseId = new BulkTemplateField('rootCauseId', this.issue, (issue) => issue.rootCauseId, (issue, value) => issue.rootCauseId = value, (id) => this.getRootCauseName(id));
    containerId = new BulkTemplateField('containerId', this.issue, (issue) => issue.containerId, (issue, value) => issue.containerId = value);
    customAttributes = new BulkTemplateField('customAttributes', this.issue, (issue) => issue.customAttributes, (issue, value) => issue.customAttributes = value);
    comment = new BulkTemplateField('comment', this.issue, (issue) => issue.comment, (issue, value) => issue.comment = value);
    
    standardBulkUpdateFields = [ this.id, this.forgeId, this.status, this.permittedStatuses, this.issueTypeId, this.issueSubtypeId, this.assignedTo, this.location, this.owner, this.dueDate, this.rootCauseId, this.containerId, this.customAttributes, this.comment ];
    customAttributeFields: { id: string, field: BulkTemplateField<string> }[] = [];

    private makeFieldForCustomAttribute(id: string)
    {
        let getCustomAttributeForIssue = (issue: BIM360Issue) =>
        {
            let attribute = issue.customAttributes.find(ca => ca.attributeDefinitionId == id);
            return attribute?.value;
        };

        let getWritableCustomAttributeForIssue = (issue: BIM360Issue) =>
        {
            let attribute = issue.customAttributes.find(ca => ca.attributeDefinitionId == id);

            if (!attribute)
            {
                attribute = { attributeDefinitionId: id, value: null };
                issue.customAttributes.push(attribute);
            }

            return attribute;
        };


        return new BulkTemplateField(this.getCustomAttrTitle(id), this.issue, (i) => getCustomAttributeForIssue(i), (i, v) => getWritableCustomAttributeForIssue(i).value = v, (v, i) => this.getCustomeAttrValue(v))
    }

    public getFieldForCustomAttribute(id: string)
    {
        let field = this.customAttributeFields.find(f => f.id == id);

        if (!field)
        {
            field = {
                id: id,
                field: this.makeFieldForCustomAttribute(id)
            };

            this.customAttributeFields.push(field);
        }

        return field;
    }

    public get allBulkUpdateFields()
    {
        return  [...this.standardBulkUpdateFields, ...this.customAttributeFields.map(f => f.field)];
    }

    public constructor(public issue: FrontendBIM360Issue, model: UIModel)
    {
        this.title = new BulkTemplateFieldWithTokens(issue.title);
        this.description = new BulkTemplateFieldWithTokens(issue.description);
        this.locationDescription = new BulkTemplateFieldWithTokens(issue.locationDescription);
        this.model = model;
    }

    getAssigneeName(assigneeId: string) {
        let user: ForgeUser | null = null;
        let role: ProjectRole | null = null;
        let company: BIM360Company | null = null;

        if(this.model?.projectUsers) { user = this.model.projectUsers[assigneeId] };
        if(this.model?.projectRoles) { role = this.model.projectRoles[assigneeId] };
        if(this.model?.companies) { company = this.model.companies[assigneeId] };

        return user?.name || role?.name || company?.name || "Not Found";
    }

    getIssueTypeName(typeId: string) {
        if(this.model?.projectNGIssueTypesDict[typeId])return this.model?.projectNGIssueTypesDict[typeId].title;
        return "";
    }

	isValid(forNewIssues: boolean) {
        if (forNewIssues)
        {
            if (!this.issueSubtypeId.value) {
                return false;
            } else if (this.status.value != BIM360IssueStatus.Draft && this.status.value != BIM360IssueStatus.Open) {
                return false;
            } else if (!this.title.forIteration(0)) {
                return false;
            }

            return true;
        }
        else
        {
            return _.find(this.allBulkUpdateFields, f => f.isChanged)
                && _.every(this.allBulkUpdateFields, f => f.isValidForBulkUpdate);
        }
    }
    
    applyBulkEdit(originalIssue: FrontendBIM360Issue)
    {
        let newIssue = classToClass(originalIssue);

        for (let field of this.allBulkUpdateFields)
        {
            if (field.isChanged)
            {
                field.updateIssue(newIssue);
            }
        }

        return newIssue;
    }

    getRootCauseName(id: string)
    {
        const extractedArrays = _.map(this.model.projectRootCauseCategories, 'rootCauses');
        const allRootCauses = _.flatten(extractedArrays);
        const rc = allRootCauses.find(c => c.id == id);
        if(rc)return rc.title;
        return "";
    }

    getCustomeAttrValue(value: any)
    {
        const allAttrDefHasOptions = this.model?.issueAttributeDefinitions.filter(attrDef => attrDef.options);
        for(const attrDef of allAttrDefHasOptions)
        {
            const theOption = attrDef.options.find(o => o.id == value);
            if(theOption) return theOption.value;
        }
        return value;
    }

    getCustomAttrTitle(id: string)
    {
        const theCustomAttr = this.model?.issueAttributeDefinitions.find(cAttr => cAttr.id == id);
        if(theCustomAttr)
        {
            return theCustomAttr.title;
        }
        return id;
    }
}